import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { getFirmDomain } from "@/shared/utils/firm";
import EntriesTable, { ColumnWidths, SortingMode, SortDirection } from "../components/EntriesTable/EntriesTable";
import EditEntryV2 from "../components/v2/EditEntryV2";
import { useAbbreviationsCache, useRestrictedWords, useMattersCache, useTasksCache, useActivitiesCache } from "@/shared/cache";
import { CombineDialog } from "../components/CombineDialog";
import { ExportValidationDialog, InvalidMatter } from "../components/ExportValidationDialog";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";
import { useEntries, Entry } from "@/shared/contexts/EntriesContext";
import { useExportEntries } from "../hooks/useExportEntries";

type Props = {
  date: Date;
  dateRange?: { from: Date | undefined; to: Date | undefined };
  sortingMode: SortingMode;
  sortDirection: SortDirection;
  onSelectionChange?: (count: number) => void;
  onExportingChange?: (isExporting: boolean) => void;
  onActionInProgressChange?: (action: "combine" | "delete" | "duplicate" | null) => void;
  onSortByMatter?: () => void;
  onSortByTime?: () => void;
  filterEntries?: <T extends { application?: string }>(entries: T[]) => T[];
  /** Map of failed export entry IDs → error messages. Lifted to parent so it survives tab switches. */
  failedExportEntries: Map<number, string>;
  setFailedExportEntries: React.Dispatch<React.SetStateAction<Map<number, string>>>;
};
export type TrackedViewHandle = {
  combineSelected: () => Promise<void>;
  deleteSelected: () => Promise<void>;
  exportSelected: () => Promise<void>;
  duplicateSelected: () => Promise<void>;
  isExporting: boolean;
};

const TrackedView = forwardRef<TrackedViewHandle, Props>(function TrackedView({ date, dateRange, sortingMode, sortDirection, onSelectionChange, onExportingChange, onActionInProgressChange, onSortByMatter, onSortByTime, filterEntries, failedExportEntries, setFailedExportEntries }: Props, ref) {
  const { makeApiRequest } = useApi();
  const { entries, exportedEntries, refreshRecords, updateEntries, mutatingIds, setMutatingIds } = useEntries();
  const { matters } = useMattersCache();
  const { tasks } = useTasksCache();
  const { activities } = useActivitiesCache();

  // Cache-backed abbreviations and restricted words
  const { abbreviationProps } = useAbbreviationsCache();
  const { getRestrictedWords } = useRestrictedWords();

  const [groupedEntries, setGroupedEntries] = useState<Record<string, Entry[]>>({});
  const [_groupedExportedEntries, setGroupedExportedEntries] = useState<Record<string, Entry[]>>({});

  const defaultColumnWidths: ColumnWidths = { date: 7, matter: 25, codes: 7, narrative: 56, units: 10 };
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => {
    try {
      const saved = localStorage.getItem("dashboard-column-widths");
      if (!saved) return defaultColumnWidths;
      const parsed = JSON.parse(saved);
      // Migrate legacy keys: task->codes, description->narrative
      const migrated: Partial<ColumnWidths> = { ...parsed };
      if (parsed.task && !parsed.codes) migrated.codes = parsed.task;
      if (parsed.description && !parsed.narrative) migrated.narrative = parsed.description;
      // Remove legacy keys that are no longer used
      delete (migrated as any).title;
      delete (migrated as any).description;
      delete (migrated as any).task;
      return { ...defaultColumnWidths, ...migrated } as ColumnWidths;
    } catch {
      return defaultColumnWidths;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("dashboard-column-widths", JSON.stringify(columnWidths));
    } catch {}
  }, [columnWidths]);

  const columnMinWidths: ColumnWidths = { date: 5, matter: 8, codes: 6, narrative: 20, units: 6 };
  const columnOrder: (keyof ColumnWidths)[] = ["date", "matter", "codes", "narrative"];

  const handleCascadingResize = (resizingColumn: keyof ColumnWidths, deltaPercentage: number) => {
    const currentIndex = columnOrder.indexOf(resizingColumn);
    if (currentIndex === -1) return;
    const newWidths = { ...columnWidths };
    const currentWidth = columnWidths[resizingColumn];
    const minWidth = columnMinWidths[resizingColumn];
    if (deltaPercentage > 0) {
      let availableSpace = 0;
      for (let i = currentIndex + 1; i < columnOrder.length; i++) {
        const key = columnOrder[i];
        const colWidth = columnWidths[key];
        const colMin = columnMinWidths[key];
        availableSpace += Math.max(0, colWidth - colMin);
      }
      const actualExpansion = Math.min(deltaPercentage, availableSpace);
      if (actualExpansion <= 0) return;
      newWidths[resizingColumn] = currentWidth + actualExpansion;
      let remaining = actualExpansion;
      for (let i = currentIndex + 1; i < columnOrder.length && remaining > 0.01; i++) {
        const key = columnOrder[i];
        const colWidth = columnWidths[key];
        const colMin = columnMinWidths[key];
        const canRemove = Math.max(0, colWidth - colMin);
        const toRemove = Math.min(remaining, canRemove);
        if (toRemove > 0.01) {
          newWidths[key] = colWidth - toRemove;
          remaining -= toRemove;
        }
      }
    } else {
      const maxShrinkage = currentWidth - minWidth;
      const actualShrinkage = Math.max(deltaPercentage, -maxShrinkage);
      if (actualShrinkage >= 0) return;
      newWidths[resizingColumn] = currentWidth + actualShrinkage;
      const spaceToGive = -actualShrinkage;
      for (let i = currentIndex + 1; i < columnOrder.length; i++) {
        const key = columnOrder[i];
        newWidths[key] = columnWidths[key] + spaceToGive;
        break;
      }
    }
    setColumnWidths(newWidths);
  };

  function hourLabelTo24Hour(label: string): number {
    if (label === "Unknown") return 99;
    const match = label.match(/^(\d+):00 (AM|PM)$/);
    if (!match) return 99;
    const hour = parseInt(match[1], 10);
    const isPM = match[2] === "PM";
    if (hour === 12) return isPM ? 12 : 0;
    return isPM ? hour + 12 : hour;
  }

  function groupEntriesByHour(rows: Entry[], direction: SortDirection): Record<string, Entry[]> {
    const unsorted: Record<string, Entry[]> = {};
    rows.forEach((row) => {
      let hourLabel = "";
      if (row.end_time) {
        const dateObj = new Date(row.end_time);
        const hours = dateObj.getUTCHours();
        const period = hours >= 12 ? "PM" : "AM";
        const displayHour = hours % 12 || 12;
        hourLabel = `${displayHour}:00 ${period}`;
      }
      const key = hourLabel || "Unknown";
      if (!unsorted[key]) unsorted[key] = [];
      unsorted[key].push(row);
    });
    const mult = direction === "asc" ? 1 : -1;
    const orderedGroups: Record<string, Entry[]> = {};
    Object.keys(unsorted)
      .sort((a, b) => mult * (hourLabelTo24Hour(a) - hourLabelTo24Hour(b)))
      .forEach((key) => { orderedGroups[key] = unsorted[key]; });
    return orderedGroups;
  }

  function groupEntriesByMatter(rows: Entry[], direction: SortDirection): Record<string, Entry[]> {
    const unsorted: Record<string, Entry[]> = {};
    rows.forEach((row) => {
      const key = row.matter || "Unknown";
      if (!unsorted[key]) unsorted[key] = [];
      unsorted[key].push(row);
    });
    const mult = direction === "asc" ? 1 : -1;
    const sorted: Record<string, Entry[]> = {};
    Object.keys(unsorted).sort((a, b) => mult * a.localeCompare(b)).forEach((key) => {
      sorted[key] = unsorted[key];
    });
    return sorted;
  }

  // Filter by single day or date range
  const isInRange = (d: Date) => {
    if (dateRange?.from || dateRange?.to) {
      const start = dateRange.from ? new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate(), 0, 0, 0, 0) : null;
      const end = dateRange.to ? new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59, 999) : null;
      if (start && end) return d >= start && d <= end;
      if (start) return d >= start;
      if (end) return d <= end;
      return true;
    }
    const sameDay = d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
    return sameDay;
  };

  const dateFilteredEntries = useMemo(() => {
    return entries.filter((e) => {
      const d = new Date(e.date);
      return isInRange(d);
    });
  }, [entries, date, dateRange]);

  const filteredEntries = useMemo(() => {
    return filterEntries ? filterEntries(dateFilteredEntries) : dateFilteredEntries;
  }, [dateFilteredEntries, filterEntries]);

  const emptyMessage = useMemo(() => {
    if (filteredEntries.length > 0) return undefined;
    // App filter is active and hiding entries
    if (dateFilteredEntries.length > 0) return "No entries match the current filter.";
    // Date filter is active (range or specific day)
    if (dateRange?.from || dateRange?.to) return "No entries for this date range.";
    if (entries.length > 0) return "No entries for this date.";
    return "No entries yet. Click + to add your first entry.";
  }, [filteredEntries.length, dateFilteredEntries.length, entries.length, dateRange]);

  const filteredExported = useMemo(() => {
    return exportedEntries.filter((e) => {
      const d = new Date(e.date);
      return isInRange(d);
    });
  }, [exportedEntries, date, dateRange]);

  useEffect(() => {
    if (sortingMode === "matter") {
      setGroupedEntries(groupEntriesByMatter(filteredEntries, sortDirection));
      setGroupedExportedEntries(groupEntriesByMatter(filteredExported, sortDirection));
    } else {
      setGroupedEntries(groupEntriesByHour(filteredEntries, sortDirection));
      setGroupedExportedEntries(groupEntriesByHour(filteredExported, sortDirection));
    }
  }, [filteredEntries, filteredExported, sortingMode, sortDirection]);

  // Selection state
  const [checkedRows, setCheckedRows] = useState<Set<number>>(new Set());
  const lastClickedId = useRef<number | null>(null);
  const relevantEntriesCount = filteredEntries.length;
  const selectAllChecked = checkedRows.size > 0 && checkedRows.size === relevantEntriesCount;
  const selectAllIndeterminate = checkedRows.size > 0 && checkedRows.size < relevantEntriesCount;

  // Flat ordered list of entry IDs matching visual display order (for shift+click range selection)
  const flatEntryIds = useMemo(() => {
    const ids: number[] = [];
    for (const key of Object.keys(groupedEntries)) {
      for (const entry of groupedEntries[key]) {
        ids.push(entry.id);
      }
    }
    return ids;
  }, [groupedEntries]);

  const onResizeColumn = useCallback((key: keyof ColumnWidths, w: number) => {
    setColumnWidths((prev) => ({ ...prev, [key]: w }));
  }, []);

  const onToggleSelectAll = useCallback(() => {
    if (selectAllChecked || selectAllIndeterminate) {
      setCheckedRows(new Set());
    } else {
      setCheckedRows(new Set(filteredEntries.map((e) => e.id)));
    }
  }, [selectAllChecked, selectAllIndeterminate, filteredEntries]);

  const onToggleRow = useCallback((id: number, e: React.MouseEvent) => {
    const hasCtrl = e.ctrlKey || e.metaKey;

    if (e.shiftKey && lastClickedId.current !== null) {
      const from = flatEntryIds.indexOf(lastClickedId.current);
      const to = flatEntryIds.indexOf(id);
      if (from !== -1 && to !== -1) {
        const start = Math.min(from, to);
        const end = Math.max(from, to);
        const rangeIds = flatEntryIds.slice(start, end + 1);
        if (hasCtrl) {
          // Ctrl+Shift+click: add range to existing selection
          setCheckedRows((prev) => {
            const next = new Set(prev);
            for (const rid of rangeIds) next.add(rid);
            return next;
          });
        } else {
          // Shift+click: replace selection with range
          setCheckedRows(new Set(rangeIds));
        }
        // Don't move anchor on shift+click — allows re-adjusting range end
        return;
      }
    }

    if (hasCtrl) {
      // Ctrl+click: toggle single entry without affecting others
      setCheckedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    } else {
      // Plain click: select only this entry
      setCheckedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    }
    lastClickedId.current = id;
  }, [flatEntryIds]);

  useEffect(() => {
    onSelectionChange?.(checkedRows.size);
  }, [checkedRows, onSelectionChange]);

  const performCombine = async (ids: number[], matter_id: string, main_entry_id: number) => {
    const nonMainIds = new Set(ids.filter(id => id !== main_entry_id));

    // Optimistic: remove non-main entries, mark main as mutating
    const rollback = updateEntries(prev => prev.filter(e => !nonMainIds.has(e.id)));
    setMutatingIds(prev => { const next = new Set(prev); next.add(main_entry_id); return next; });
    setCheckedRows(new Set());
    onActionInProgressChange?.("combine");

    try {
      await logger.trackOperation(
        'Combine entries',
        async () => {
          const resp = await makeApiRequest({
            endpoint: "/records/combine",
            method: "POST",
            body: { ids, matter_id, main_entry_id },
          });
          if (resp.error) throw new Error(String(resp.error));
        },
        { entryCount: ids.length, matterId: matter_id, mainEntryId: main_entry_id }
      );
      toast.success("Entries combined", {
        description: `${ids.length} entries merged into one`,
      });
      refreshRecords();
    } catch (e) {
      rollback();
      refreshRecords();
      if (e instanceof Error) {
        logger.captureException(e, { action: 'combine', entryCount: ids.length });
      }
      toast.error("Failed to combine entries", {
        duration: TOAST_DURATION.error,
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setMutatingIds(prev => { const next = new Set(prev); next.delete(main_entry_id); return next; });
      onActionInProgressChange?.(null);
    }
  };

  const combineSelected = async () => {
    if (checkedRows.size < 2) {
      logger.warn("Combine validation failed - need at least 2 entries", {
        action: 'combine',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select at least 2 entries to combine");
      return;
    }

    // Always open the combine dialog for review, reorder, and confirmation
    setCombineDialogOpen(true);
  };

  const { exportEntries, exportingIds, isExporting } = useExportEntries({
    makeApiRequest,
    onComplete: ({ exportedIds, failedEntries }) => {
      setCheckedRows(new Set());

      // Merge failed entries into the persistent map
      setFailedExportEntries(prev => {
        const updated = new Map(prev);
        for (const id of exportedIds) updated.delete(id);
        for (const f of failedEntries) updated.set(Number(f.id), f.error);
        return updated;
      });

      refreshRecords();

      const exportedCount = exportedIds.length;
      const totalCount = exportedCount + failedEntries.length;
      if (exportedCount === 0 && failedEntries.length > 0) {
        const uniqueErrors = [...new Set(failedEntries.map(f => f.error))];
        toast.error("Export failed", {
          duration: TOAST_DURATION.error,
          description: uniqueErrors.join("; "),
        });
      } else if (exportedCount === 0) {
        toast.error("No entries exported", {
          duration: TOAST_DURATION.error,
          description: "Ensure entries are complete before exporting",
        });
      } else if (failedEntries.length > 0) {
        toast.success(`Exported ${exportedCount} of ${totalCount} entries`, {
          description: `${failedEntries.length} ${failedEntries.length === 1 ? 'entry' : 'entries'} failed`,
        });
      } else if (exportedCount === 1) {
        toast.success("Entry exported");
      } else {
        toast.success(`Exported ${exportedCount} entries`);
      }
    },
  });

  useEffect(() => {
    onExportingChange?.(isExporting);
  }, [isExporting, onExportingChange]);

  const performExport = async (ids: number[]) => {
    await exportEntries(ids);
  };

  const exportSelected = async () => {
    if (checkedRows.size === 0) {
      logger.warn("Export validation failed - no entries selected", {
        action: 'export',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select entries to export");
      return;
    }
    const ids = filteredEntries.filter(e => checkedRows.has(e.id)).map(e => e.id);

    // Validate export - check for matters not assigned to this professional
    // Skip for Tyson Mendes until validation endpoint is fixed for their setup
    if (getFirmDomain() !== "tysonmendes") {
      try {
        const resp = await makeApiRequest({ endpoint: "/records/validateExport", method: "POST", body: { ids } });
        if (resp.data?.invalidMatters?.length > 0) {
          setPendingExportIds(ids);
          setInvalidMatters(resp.data.invalidMatters);
          setExportValidationOpen(true);
          return;
        }
      } catch {
        // Validation failed silently - proceed with export
      }
    }

    await performExport(ids);
  };

  const deleteSelected = async () => {
    if (checkedRows.size === 0) {
      logger.warn("Delete validation failed - no entries selected", {
        action: 'delete',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select entries to delete");
      return;
    }
    const ids = filteredEntries.filter(e => checkedRows.has(e.id)).map(e => e.id);
    const idSet = new Set(ids);

    // Optimistic: remove entries immediately
    const rollback = updateEntries(prev => prev.filter(e => !idSet.has(e.id)));
    setCheckedRows(new Set());
    onActionInProgressChange?.("delete");

    try {
      const resp = await makeApiRequest({ endpoint: "/records/delete", method: "POST", body: { ids } });
      if (resp.error) throw new Error(String(resp.error));
      // Clear any export failures for deleted entries
      setFailedExportEntries(prev => {
        if (!ids.some(id => prev.has(id))) return prev;
        const next = new Map(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      toast.success(`Deleted ${ids.length} ${ids.length === 1 ? 'entry' : 'entries'}`);
      refreshRecords();
    } catch (e) {
      rollback();
      refreshRecords();
      if (e instanceof Error) {
        logger.captureException(e, { action: 'delete', entryCount: ids.length });
      }
      toast.error("Failed to delete entries", {
        duration: TOAST_DURATION.error,
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      onActionInProgressChange?.(null);
    }
  };

  const duplicateSelected = async () => {
    if (checkedRows.size === 0) {
      logger.warn("Duplicate validation failed - no entries selected", {
        action: 'duplicate',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select entries to duplicate");
      return;
    }
    const ids = filteredEntries.filter(e => checkedRows.has(e.id)).map(e => e.id);

    // Mark source rows as mutating (no optimistic add since we don't know new IDs)
    setMutatingIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
    onActionInProgressChange?.("duplicate");

    try {
      const resp = await makeApiRequest({ endpoint: "/records/duplicate", method: "POST", body: { ids } });
      if (resp.error) throw new Error(String(resp.error));
      setCheckedRows(new Set());
      toast.success(`Duplicated ${ids.length} ${ids.length === 1 ? 'entry' : 'entries'}`);
      refreshRecords();
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'duplicate', entryCount: ids.length });
      }
      toast.error("Failed to duplicate entries", {
        duration: TOAST_DURATION.error,
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setMutatingIds(prev => { const next = new Set(prev); ids.forEach(id => next.delete(id)); return next; });
      onActionInProgressChange?.(null);
    }
  };

  const handleDuplicateEntry = async (entry: {
    date: string;
    matter: string;
    matter_id: string;
    description: string;
    duration: number;
    task: string;
    activity: string;
    document_name: string | null;
    client: string | null;
    units_seconds: number;
    start_time: string | null;
    end_time: string | null;
  }, originalId: number) => {
    const resp = await makeApiRequest({
      endpoint: "/records/duplicate",
      method: "POST",
      body: {
        entry: {
          date: entry.date,
          matter_id: entry.matter_id,
          description: entry.description,
          units: entry.duration,
          task_code: entry.task?.trim() || null,
          activity_code: entry.activity?.trim() || null,
          document_name: entry.document_name?.trim() || null,
          client: entry.client?.trim() || null,
          units_seconds: entry.units_seconds || 0,
          start_time: entry.start_time || null,
          end_time: entry.end_time || null,
          duplicated_from_id: originalId,
        }
      },
    });
    if (resp.error) throw new Error(String(resp.error));
    await refreshRecords();
  };

  // Edit dialog state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
  const [selectedExportError, setSelectedExportError] = useState<string | null>(null);
  // Combine dialog state
  const [combineDialogOpen, setCombineDialogOpen] = useState(false);
  const combineEntries = useMemo(
    () => filteredEntries.filter((e) => checkedRows.has(e.id)),
    [filteredEntries, checkedRows]
  );

  // Export validation dialog state
  const [exportValidationOpen, setExportValidationOpen] = useState(false);
  const [invalidMatters, setInvalidMatters] = useState<InvalidMatter[]>([]);
  const [pendingExportIds, setPendingExportIds] = useState<number[]>([]);

  const onRowClick = useCallback((entry: Entry) => {
    if (exportingIds.has(entry.id)) return;
    setSelectedRowData({
      id: entry.id,
      date: entry.date,
      matter_name: entry.matter,
      matter_id: entry.matter_id || "",
      description: entry.description,
      duration: entry.duration,
      task: entry.task,
      activity: entry.activity || "",
      document_name: entry.document_name || "",
      units_seconds: entry.units_seconds || 0,
      start_time: entry.start_time || "",
      end_time: entry.end_time || "",
      application: entry.application || "",
      attorney_feedback: entry.attorney_feedback || null,
    });
    setShowEditModal(true);
    setSelectedExportError(failedExportEntries.get(Number(entry.id)) || null);
  }, [failedExportEntries, exportingIds]);

  const handleResolveFeedback = async (entryId: number) => {
    try {
      const resp = await makeApiRequest({
        endpoint: `/records/${entryId}/resolve-feedback`,
        method: "POST",
      });
      if (resp.error) throw new Error(String(resp.error));
      await refreshRecords();
      // Update selectedRowData to clear the feedback
      setSelectedRowData((prev: any) => prev ? { ...prev, attorney_feedback: null } : null);
      toast.success("Feedback resolved");
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'resolveFeedback', entryId });
      }
      toast.error("Failed to resolve feedback", { duration: TOAST_DURATION.error });
    }
  };

  const onSaveEditEntry = async (
    updatedEntry: {
      id: number; date: string; matter: string; matter_id: string; description: string; duration: number; task: string; activity: string;
    },
    shouldExport: boolean
  ) => {
    // Optimistic: update entry fields immediately
    const rollback = updateEntries(prev =>
      prev.map(e => {
        if (e.id !== updatedEntry.id) return e;
        const selected = matters.find(m => m.matter_id === updatedEntry.matter_id);
        return {
          ...e,
          matter: selected?.description ?? e.matter,
          matter_id: updatedEntry.matter_id,
          matter_name: selected?.matter ?? e.matter_name,
          client: selected?.client ?? e.client,
          description: updatedEntry.description,
          duration: updatedEntry.duration,
          task: updatedEntry.task,
          activity: updatedEntry.activity,
        };
      })
    );
    setMutatingIds(prev => { const next = new Set(prev); next.add(updatedEntry.id); return next; });
    setShowEditModal(false);

    try {
      const resp = await makeApiRequest({ endpoint: "/records/edit", method: "POST", body: { ...updatedEntry } });
      if (resp.error) throw new Error(String(resp.error));

      // Clear export failure for this entry since it was just edited
      setFailedExportEntries(prev => {
        if (!prev.has(updatedEntry.id)) return prev;
        const next = new Map(prev);
        next.delete(updatedEntry.id);
        return next;
      });

      refreshRecords();

      // If shouldExport, validate first then export
      if (shouldExport) {
        const ids = [updatedEntry.id];

        // Validate export
        try {
          const validationResp = await makeApiRequest({ endpoint: "/records/validateExport", method: "POST", body: { ids } });
          if (validationResp.data?.invalidMatters?.length > 0) {
            setPendingExportIds(ids);
            setInvalidMatters(validationResp.data.invalidMatters);
            setExportValidationOpen(true);
            return;
          }
        } catch {
          // Validation failed silently - proceed with export
        }

        await performExport(ids);
      }
    } catch (e) {
      rollback();
      refreshRecords();
      if (e instanceof Error) {
        logger.captureException(e, { action: 'editEntry', entryId: updatedEntry.id, shouldExport });
      }
      toast.error("Failed to save entry", {
        duration: TOAST_DURATION.error,
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setMutatingIds(prev => { const next = new Set(prev); next.delete(updatedEntry.id); return next; });
    }
  };

  useImperativeHandle(ref, () => ({ combineSelected, deleteSelected, exportSelected, duplicateSelected, isExporting }), [combineSelected, deleteSelected, exportSelected, duplicateSelected, isExporting]);

  return (
    <>
      <EntriesTable
        key={dateRange ? `${dateRange.from?.toDateString()}-${dateRange.to?.toDateString()}` : date.toDateString()}
        entries={filteredEntries}
        groupedEntries={groupedEntries}
        columnWidths={columnWidths}
        onResizeColumn={onResizeColumn}
        onCascadingResize={handleCascadingResize}
        checkedRows={checkedRows}
        selectAllChecked={selectAllChecked}
        selectAllIndeterminate={selectAllIndeterminate}
        onToggleSelectAll={onToggleSelectAll}
        onToggleRow={onToggleRow}
        onRowClick={onRowClick}
        onSortByMatter={onSortByMatter}
        onSortByTime={onSortByTime}
        mode="entries"
        matters={matters}
        sortingMode={sortingMode}
        sortDirection={sortDirection}
        failedExportEntries={failedExportEntries}
        exportingEntryIds={exportingIds}
        mutatingIds={mutatingIds}
        emptyMessage={emptyMessage}
        getRestrictedWords={getRestrictedWords}
        autoScrollToBottom={!dateRange && date.toDateString() === new Date().toDateString()}
      />
      {selectedRowData && (
        <EditEntryV2
          onSave={onSaveEditEntry}
          onDuplicate={handleDuplicateEntry}
          matters={matters}
          tasks={tasks}
          activities={activities}
          selectedRowData={selectedRowData}
          open={showEditModal}
          onOpenChange={setShowEditModal}
          abbreviations={abbreviationProps}
          attorneyFeedback={selectedRowData.attorney_feedback}
          onResolveFeedback={() => handleResolveFeedback(selectedRowData.id)}
          exportError={selectedExportError}
        />
      )}
      <CombineDialog
        open={combineDialogOpen}
        onOpenChange={setCombineDialogOpen}
        entries={combineEntries}
        matters={matters}
        onConfirm={(ids, matterId, mainId) => {
          setCombineDialogOpen(false);
          performCombine(ids, matterId, mainId);
        }}
        onCancel={() => setCombineDialogOpen(false)}
      />
      <ExportValidationDialog
        open={exportValidationOpen}
        onOpenChange={setExportValidationOpen}
        invalidMatters={invalidMatters}
        onProceed={() => {
          setExportValidationOpen(false);
          performExport(pendingExportIds);
          setPendingExportIds([]);
          setInvalidMatters([]);
        }}
        onCancel={() => {
          setExportValidationOpen(false);
          setPendingExportIds([]);
          setInvalidMatters([]);
        }}
      />
    </>
  );
});

export default TrackedView;
